/**
 * Transform Engine
 *
 * Reads the uploaded Excel file, delegates to the appropriate profile
 * transformer, runs validation, and returns everything the UI needs.
 */
import * as XLSX from 'xlsx';
import { PROFILES } from '../profiles/index';
import { validate } from './validator';
import { buildPlan, applyPlan } from './ingest';
import { transformBudget } from '../profiles/gl9300c-budget';
import { transformJE } from '../profiles/gl8610xl-je';
import { transformPayroll } from '../profiles/gl8610xl-payroll';

const TRANSFORMERS = {
  'gl9300c-budget': transformBudget,
  'gl8610xl-je': transformJE,
  'gl8610xl-payroll': transformPayroll,
};

/**
 * Main entry point: reads file, transforms, validates.
 *
 * `plan` is an optional ingest plan (from src/lib/ingest.js) describing which
 * sheet, header row, and column mapping to use for messy source files. When
 * omitted, one is auto-detected. The payroll profile has its own multi-sheet
 * parser and ignores the plan entirely.
 *
 * Returns { profile, data, validation, workbook }
 */
export async function processFile(file, profileKey, plan = null) {
  const profileDef = PROFILES[profileKey];
  if (!profileDef) throw new Error(`Unknown profile: ${profileKey}`);

  // Read file into ArrayBuffer
  const buffer = await file.arrayBuffer();
  const sourceWb = XLSX.read(buffer, { type: 'array', cellDates: true });

  // Build canonical-keyed rows via the messy-file ingest analyzer.
  // Payroll re-parses the workbook itself, so its data set is unused there.
  const ingestPlan = plan ?? buildPlan(sourceWb, profileKey);
  const data = applyPlan(sourceWb, ingestPlan);

  // Run the profile-specific transform
  const transformer = TRANSFORMERS[profileKey];
  if (!transformer) throw new Error(`No transformer for profile: ${profileKey}`);

  const { workbook, rows, meta } = transformer(data, sourceWb);

  // Run validation
  const validation = validate(rows, profileKey, meta);

  return {
    profile: profileKey,
    data,
    validation,
    workbook,
  };
}
